<template>
	<div k-data-id="adminSidebar-container" class="h-screen relative" id="sidebar">
		<!-- Mobile Top Navigation Bar -->
		<div
			k-data-id="adminSidebar-mobileTopNav"
			v-if="isMobile && !showMobileSidebar"
			class="fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between border-theme-primary"
			style="background-color: #000715; color: white">
			<!-- Burger menu to show sidebar -->
			<button
				k-data-id="adminSidebar-mobileTopNav-burgerBtn"
				@click="showMobileSidebar = true"
				class="p-2 bg-theme-secondary hover:bg-theme-hover rounded-md border border-theme-secondary">
				<PhList class="w-6 h-6 text-theme-primary" weight="bold" />
			</button>

			<!-- Logo in center -->
			<img
				k-data-id="adminSidebar-mobileTopNav-logo"
				class="h-10 w-10"
				src="../../assets/images/k-icon.png"
				alt="Komunily Icon" />

			<!-- Empty space for balance -->
			<div class="w-10"></div>
		</div>

		<!-- Mobile Sidebar Overlay -->
		<div
			k-data-id="adminSidebar-mobileOverlay"
			v-if="isMobile && showMobileSidebar"
			@click="showMobileSidebar = false"
			class="fixed inset-0 bg-black bg-opacity-50 z-40"></div>

		<!-- Profile dropdown menu (positioned outside any containers to avoid clipping) -->
		<div
			k-data-id="adminSidebar-profileMenu"
			id="profileMenu"
			v-if="profileMenu"
			@mouseleave="profileMenu = false"
			class="fixed bg-theme-primary flex flex-col rounded-md shadow-xl z-[60]"
			:style="{
				bottom: '1rem',
				left: collapsed ? '5rem' : '13rem'
			}">
			<n-menu :options="userNav" @update:value="handleProfileMenuSelect" />
		</div>

		<!-- Main sidebar container with manual width control -->
		<div
			k-data-id="adminSidebar-mainContent"
			:class="[
				'border-r transition-all duration-200 relative border-theme-primary text-white',
				isMobile
					? 'fixed z-50 w-screen h-screen top-0 left-0'
					: 'h-full ' + (collapsed ? 'w-16' : 'w-[200px]'),
				isMobile && !showMobileSidebar ? 'hidden' : ''
			]"
			style="background-color: #000715">
			<!-- Toggle Button positioned on the right edge like MainSideNav -->
			<button
				k-data-id="adminSidebar-collapseBtn"
				v-if="!isMobile"
				@click="collapsed = !collapsed"
				class="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 bg-theme-primary border border-theme-secondary rounded-full flex items-center justify-center hover:bg-theme-secondary transition-colors duration-200 z-40"
				:aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'">
				<component
					:is="collapsed ? PhCaretRight : PhCaretLeft"
					class="w-3 h-3 text-theme-primary hover:text-blue-500"
					weight="bold" />
			</button>
			<!-- Main sidebar content wrapper with proper scroll handling -->
			<div class="sidebar-content-wrapper h-full flex flex-col">
				<!-- Top fixed section with logo/up arrow -->
				<div class="px-2 py-3 flex-shrink-0">
					<!-- Mobile: Up arrow to close sidebar, Desktop: Logo -->
					<div v-if="isMobile" class="flex items-center justify-between px-2">
						<!-- Up Arrow to close sidebar -->
						<button
							k-data-id="adminSidebar-mobile-closeBtn"
							@click="showMobileSidebar = false"
							class="p-2 bg-theme-secondary hover:bg-theme-hover rounded-md border border-theme-secondary">
							<PhCaretUp class="w-6 h-6 text-theme-primary" weight="bold" />
						</button>

						<img class="h-10 w-10" src="../../assets/images/k-icon.png" alt="Komunily Icon" />

						<!-- Empty space for balance -->
						<div class="w-10"></div>
					</div>
					<div v-else>
						<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
							<template #trigger>
								<a
									k-data-id="adminSidebar-exitAdminLink"
									:href="'https://' + root"
									@click="handleNavClick"
									class="sidebar-link rounded-md hover:bg-theme-hover"
									:class="isCollapsed ? 'justify-center' : ''">
									<img
										:class="isCollapsed ? 'h-8 w-8' : 'sidebar-icon'"
										src="../../assets/images/k-icon.png"
										alt="Komunily Icon" />
									<span v-if="!isCollapsed" class="sidebar-text">Exit Admin</span>
								</a>
							</template>
							<span>Exit Admin</span>
						</n-popover>
					</div>
				</div>

				<!-- Scrollable middle section with main nav and communities -->
				<div class="sidebar-scrollable-section">
					<!-- Main Navigation Menu -->
					<div class="px-2 pb-2">
						<div class="flex flex-col gap-2">
							<!-- Home (conditional) -->
							<n-popover
								v-if="tenant?.pages?.home"
								trigger="hover"
								:disabled="!isCollapsed"
								placement="right">
								<template #trigger>
									<router-link
										k-data-id="adminSidebar-nav-homeLink"
										to="/"
										class="sidebar-link"
										:class="isCollapsed ? 'justify-center' : ''"
										@click="handleNavClick">
										<PhHouseLine
											:class="[
												'sidebar-icon',
												isActiveRoute('home') ? 'text-blue-600' : 'text-theme-muted'
											]"
											weight="light" />
										<span
											v-if="!isCollapsed"
											:class="['sidebar-text', isActiveRoute('home') ? 'text-blue-600' : '']">
											Home
										</span>
									</router-link>
								</template>
								<span>Home</span>
							</n-popover>

							<!-- Dashboard -->
							<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<router-link
										k-data-id="adminSidebar-nav-dashboardLink"
										:to="tenant?.pages?.home ? '/dashboard' : '/'"
										class="sidebar-link hover:bg-theme-hover"
										:class="isCollapsed ? 'justify-center' : ''"
										@click="handleNavClick">
										<PhChalkboardTeacher
											:class="['sidebar-icon', isActiveRoute('dashboard') ? 'text-blue-600' : '']"
											weight="light" />
										<span
											v-if="!isCollapsed"
											:class="['sidebar-text', isActiveRoute('dashboard') ? 'text-blue-600' : '']">
											Dashboard
										</span>
									</router-link>
								</template>
								<span>Dashboard</span>
							</n-popover>

							<!-- Learning -->
							<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<div
										class="flex items-center justify-between w-full rounded-md hover:bg-theme-hover">
										<router-link
											k-data-id="adminSidebar-nav-learningLink"
											to="/admin/learning"
											class="sidebar-link flex-1"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhGraduationCap
												:class="['sidebar-icon', isActiveRoute('learning') ? 'text-blue-600' : '']"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="['sidebar-text', isActiveRoute('learning') ? 'text-blue-600' : '']">
												Learning
											</span>
										</router-link>
										<a
											k-data-id="adminSidebar-nav-learning-viewLink"
											v-if="!isCollapsed"
											href="/learning/"
											target="_blank"
											class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2 p-1"
											title="View in User App"
											@click.stop>
											<PhEye class="w-4 h-4" weight="light" />
										</a>
									</div>
								</template>
								<div class="flex items-center gap-2">
									<span>Learning</span>
									<a
										href="/learning/"
										target="_blank"
										class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2"
										title="View in User App">
										<PhEye class="w-4 h-4" weight="light" />
									</a>
								</div>
							</n-popover>

							<!-- Events -->
							<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<div
										class="flex items-center justify-between w-full rounded-md hover:bg-theme-hover">
										<router-link
											k-data-id="adminSidebar-nav-eventsLink"
											to="/admin/events"
											class="sidebar-link flex-1"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhCalendarCheck
												:class="['sidebar-icon', isActiveRoute('events') ? 'text-blue-600' : '']"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="['sidebar-text', isActiveRoute('events') ? 'text-blue-600' : '']">
												Events
											</span>
										</router-link>
										<a
											k-data-id="adminSidebar-nav-events-viewLink"
											v-if="!isCollapsed"
											href="/events/"
											target="_blank"
											class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2 p-1"
											title="View in User App"
											@click.stop>
											<PhEye class="w-4 h-4" weight="light" />
										</a>
									</div>
								</template>
								<div class="flex items-center gap-2 hover:bg-theme-hover">
									<span>Events</span>
									<a
										href="/events/"
										target="_blank"
										class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2"
										title="View in User App">
										<PhEye class="w-4 h-4" weight="light" />
									</a>
								</div>
							</n-popover>

							<!-- Community -->
							<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<div
										class="flex items-center justify-between w-full rounded-md hover:bg-theme-hover">
										<router-link
											k-data-id="adminSidebar-nav-communityLink"
											to="/admin/community"
											class="sidebar-link flex-1"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhUsersThree
												:class="['sidebar-icon', isActiveRoute('community') ? 'text-blue-600' : '']"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('community') ? 'text-blue-600' : ''
												]">
												Community
											</span>
										</router-link>
										<a
											k-data-id="adminSidebar-nav-community-viewLink"
											v-if="!isCollapsed"
											href="/community/"
											target="_blank"
											class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2 p-1"
											title="View in User App"
											@click.stop>
											<PhEye class="w-4 h-4" weight="light" />
										</a>
									</div>
								</template>
								<div class="flex items-center gap-2">
									<span>Community</span>
									<a
										href="/community/"
										target="_blank"
										class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2"
										title="View in User App">
										<PhEye class="w-4 h-4" weight="light" />
									</a>
								</div>
							</n-popover>

							<!-- Articles -->
							<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<div
										class="flex items-center justify-between w-full rounded-md hover:bg-slate-600">
										<router-link
											k-data-id="adminSidebar-nav-articlesLink"
											to="/admin/articles"
											class="sidebar-link flex-1"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhArticle
												:class="['sidebar-icon', isActiveRoute('articles') ? 'text-blue-600' : '']"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="['sidebar-text', isActiveRoute('articles') ? 'text-blue-600' : '']">
												Articles
											</span>
										</router-link>
										<a
											k-data-id="adminSidebar-nav-articles-viewLink"
											v-if="!isCollapsed"
											:href="(tenant?.value?.articleTypes?.[0]?.path || '/articles') + '/'"
											target="_blank"
											class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2 p-1"
											title="View in User App"
											@click.stop>
											<PhEye class="w-4 h-4" weight="light" />
										</a>
									</div>
								</template>
								<div class="flex items-center gap-2">
									<span>Articles</span>
									<a
										:href="(tenant?.value?.articleTypes?.[0]?.path || '/articles') + '/'"
										target="_blank"
										class="flex items-center gap-1 text-blue-500 hover:text-blue-700 ml-2"
										title="View in User App">
										<PhEye class="w-4 h-4" weight="light" />
									</a>
								</div>
							</n-popover>

							<!-- Admin Tools Section (conditional) -->
							<div
								v-if="user?.role?.administrator || user?.role?.collaborator || true"
								class="mt-2">
								<div
									k-data-id="adminSidebar-adminToolsTitle"
									v-if="!isCollapsed"
									class="text-xs mb-2 pl-2 text-theme-muted">
									ADMIN TOOLS
								</div>

								<!-- Webinars -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-webinarsLink"
											to="/admin/webinars"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhDesktop
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/webinars') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/webinars') ? 'text-blue-600' : ''
												]">
												Webinars
											</span>
										</router-link>
									</template>
									<span>Webinars</span>
								</n-popover>

								<!-- KAi -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-kaiLink"
											to="/admin/kai"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhSparkle
												:class="['sidebar-icon', isActiveRoute('admin/kai') ? 'text-blue-600' : '']"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/kai') ? 'text-blue-600' : ''
												]">
												KAi
											</span>
										</router-link>
									</template>
									<span>KAi</span>
								</n-popover>

								<!-- Pages -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-pagesLink"
											to="/admin/pages"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhFileText
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/pages') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/pages') ? 'text-blue-600' : ''
												]">
												Pages
											</span>
										</router-link>
									</template>
									<span>Pages</span>
								</n-popover>

								<!-- Forms -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-formsLink"
											to="/admin/forms"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhFiles
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/forms') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/forms') ? 'text-blue-600' : ''
												]">
												Forms
											</span>
										</router-link>
									</template>
									<span>Forms</span>
								</n-popover>

								<!-- Assessments -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-assessmentsLink"
											to="/admin/assessments"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhCheckCircle
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/assessments') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/assessments') ? 'text-blue-600' : ''
												]">
												Assessments
											</span>
										</router-link>
									</template>
									<span>Assessments</span>
								</n-popover>

								<!-- Resources -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-resourcesLink"
											to="/admin/resources"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhFolder
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/resources') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/resources') ? 'text-blue-600' : ''
												]">
												Resources
											</span>
										</router-link>
									</template>
									<span>Resources</span>
								</n-popover>

								<!-- Notifications -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-notificationsLink"
											to="/admin/notifications"
											class="sidebar-link hover:bg-slate-600"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhBell
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/notifications') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/notifications') ? 'text-blue-600' : ''
												]">
												Notifications
											</span>
										</router-link>
									</template>
									<span>Notifications</span>
								</n-popover>

								<!-- FAQs -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-faqsLink"
											to="/admin/faqs"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhChartBar
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/faqs') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/faqs') ? 'text-blue-600' : ''
												]">
												FAQs
											</span>
										</router-link>
									</template>
									<span>FAQs</span>
								</n-popover>

								<!-- Redirects -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-redirectsLink"
											to="/admin/redirects"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhArrowsClockwise
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/redirects') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/redirects') ? 'text-blue-600' : ''
												]">
												Redirects
											</span>
										</router-link>
									</template>
									<span>Redirects</span>
								</n-popover>

								<!-- Users -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-usersLink"
											to="/admin/users"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhUsersThree
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/users') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/users') ? 'text-blue-600' : ''
												]">
												Users
											</span>
										</router-link>
									</template>
									<span>Users</span>
								</n-popover>

								<!-- Access -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-accessLink"
											to="/admin/access"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhLock
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/access') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/access') ? 'text-blue-600' : ''
												]">
												Access
											</span>
										</router-link>
									</template>
									<span>Access</span>
								</n-popover>

								<!-- Companies -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-companiesLink"
											to="/admin/companies"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhBuildings
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/companies') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/companies') ? 'text-blue-600' : ''
												]">
												Companies
											</span>
										</router-link>
									</template>
									<span>Companies</span>
								</n-popover>

								<!-- Settings -->
								<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
									<template #trigger>
										<router-link
											k-data-id="adminSidebar-nav-settingsLink"
											to="/admin/settings"
											class="sidebar-link hover:bg-theme-hover"
											:class="isCollapsed ? 'justify-center' : ''"
											@click="handleNavClick">
											<PhGear
												:class="[
													'sidebar-icon',
													isActiveRoute('admin/settings') ? 'text-blue-600' : ''
												]"
												weight="light" />
											<span
												v-if="!isCollapsed"
												:class="[
													'sidebar-text',
													isActiveRoute('admin/settings') ? 'text-blue-600' : ''
												]">
												Settings
											</span>
										</router-link>
									</template>
									<span>Settings</span>
								</n-popover>
							</div>

							<!-- Search -->
							<!-- <n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
								<template #trigger>
									<div
										class="sidebar-link cursor-pointer"
										:class="isCollapsed ? 'justify-center' : ''"
										id="searchKomunily"
										@click="showSearch = !showSearch">
										<PhMagnifyingGlass
											:class="['sidebar-icon', showSearch ? 'text-blue-600' : '']"
											weight="light" />
										<span
											v-if="!isCollapsed"
											:class="['sidebar-text', showSearch ? 'text-blue-600' : '']">
											Search
										</span>
									</div>
								</template>
								<span>Search</span>
							</n-popover> -->
						</div>
					</div>
				</div>

				<!-- Bottom fixed user section -->
				<div class="px-2 pb-2 mt-auto">
					<div
						k-data-id="adminSidebar-userSectionTitle"
						v-if="!isCollapsed"
						class="text-xs mb-2 pl-2">
						USER
					</div>
					<div class="flex flex-col gap-2">
						<!-- Messages -->
						<n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
							<template #trigger>
								<a
									k-data-id="adminSidebar-nav-messagesLink"
									href="/community/messages/"
									target="_blank"
									class="sidebar-link hover:bg-theme-hover"
									:class="isCollapsed ? 'justify-center' : ''">
									<PhChatCircleDots class="sidebar-icon" weight="light" />
									<span v-if="!isCollapsed" class="sidebar-text">Messages</span>
								</a>
							</template>
							<span>Messages</span>
						</n-popover>

						<!-- Notifications -->
						<!-- <n-popover trigger="hover" :disabled="!isCollapsed" placement="right">
							<template #trigger>
								<div
									@click=";(showNotify = !showNotify), (showSearch = false)"
									class="sidebar-link cursor-pointer"
									:class="isCollapsed ? 'justify-center' : ''">
									<PhBell
										:class="['sidebar-icon', showNotify ? 'text-blue-600' : '']"
										weight="light" />
									<span
										v-if="!isCollapsed"
										:class="['sidebar-text', showNotify ? 'text-blue-600' : '']">
										Notifications
									</span>
								</div>
							</template>
							<span>Notifications</span>
						</n-popover> -->

						<!-- Profile with dropdown -->
						<div>
							<!-- Mobile: Expandable inline menu -->
							<div v-if="isMobile">
								<div
									k-data-id="adminSidebar-mobile-userMenuTrigger"
									@click="mobileUserExpanded = !mobileUserExpanded"
									class="sidebar-link cursor-pointer flex items-center justify-between">
									<div class="flex items-center">
										<div
											v-if="(user?.firstName && user?.lastName) || user?.profileImage"
											class="rounded-full text-white w-6 h-6 flex items-center justify-center text-xs overflow-hidden"
											:class="
												!user?.profileImage
													? isUserSectionActive()
														? 'bg-blue-600'
														: 'bg-yellow-500'
													: ''
											">
											<img
												class="rounded-full object-cover w-full h-full"
												v-if="user?.profileImage"
												:src="user?.profileImage" />
											<div v-else>
												{{ user?.firstName?.charAt(0) + user?.lastName?.charAt(0) }}
											</div>
										</div>
										<div v-else>
											<PhUser
												:class="['w-6 h-6', isUserSectionActive() ? 'text-blue-600' : '']"
												weight="light" />
										</div>
										<span :class="['sidebar-text', isUserSectionActive() ? 'text-blue-600' : '']">
											User
										</span>
									</div>
									<PhCaretDown
										class="w-4 h-4 transition-transform duration-200"
										:class="mobileUserExpanded ? 'rotate-180' : ''"
										weight="bold" />
								</div>

								<!-- Mobile User Submenu -->
								<div
									k-data-id="adminSidebar-mobile-userSubmenu"
									v-if="mobileUserExpanded"
									class="ml-6 mt-2 flex flex-col gap-1">
									<!-- Dark Mode Toggle -->
									<div
										k-data-id="adminSidebar-mobile-darkModeToggle"
										class="flex items-center justify-between py-2 px-2 rounded-lg transition-colors hover:bg-theme-hover">
										<div class="flex items-center gap-3">
											<component
												:is="isDark ? PhMoon : PhSun"
												class="w-4 h-4 text-theme-secondary"
												weight="light" />
											<span class="text-sm font-medium mr-2 text-theme-primary">Dark mode</span>
										</div>
										<n-switch :value="isDark" @update:value="toggleTheme" size="small" />
									</div>
									<div class="my-2 border-b border-theme-secondary"></div>
									<a
										k-data-id="adminSidebar-mobile-profileLink"
										href="/user/profile"
										class="sidebar-link text-sm hover:bg-theme-hover"
										@click="handleNavClick">
										<PhUser class="w-4 h-4 text-theme-muted mr-2" weight="light" />
										<span>Profile</span>
									</a>
									<a
										k-data-id="adminSidebar-mobile-notificationsLink"
										href="/user/notifications"
										class="sidebar-link text-sm hover:bg-theme-hover"
										@click="handleNavClick">
										<PhBell class="w-4 h-4 text-theme-muted mr-2" weight="light" />
										<span>Notifications</span>
									</a>
									<a
										k-data-id="adminSidebar-mobile-studioLink"
										href="/user/studio"
										class="sidebar-link text-sm hover:bg-theme-hover"
										@click="handleNavClick">
										<PhFilmStrip class="w-4 h-4 text-theme-muted mr-2" weight="light" />
										<span>Studio</span>
									</a>
									<a
										k-data-id="adminSidebar-mobile-bookmarksLink"
										href="/user/bookmarks"
										class="sidebar-link text-sm hover:bg-theme-hover"
										@click="handleNavClick">
										<PhBookmark class="w-4 h-4 text-theme-muted mr-2" weight="light" />
										<span>Bookmarks</span>
									</a>
									<a
										k-data-id="adminSidebar-mobile-companyLink"
										href="/user/company/profile"
										class="sidebar-link text-sm hover:bg-theme-hover"
										@click="handleNavClick">
										<PhBuildings class="w-4 h-4 text-theme-muted mr-2" weight="light" />
										<span>Company</span>
									</a>
									<div
										k-data-id="adminSidebar-mobile-logoutBtn"
										@click="logout"
										class="sidebar-link text-sm cursor-pointer text-red-500">
										<PhSignOut class="w-4 h-4 text-red-500 mr-2" weight="light" />
										<span>Logout</span>
									</div>
								</div>
							</div>

							<!-- Desktop: Dropdown menu -->
							<div
								k-data-id="adminSidebar-profileTrigger"
								v-else
								@click.stop="handleProfileClick"
								@mouseover="handleProfileMouseOver"
								id="profileMenuTrigger"
								class="sidebar-link cursor-pointer rounded-md hover:bg-theme-hover"
								:class="isCollapsed ? 'justify-center' : ''">
								<div v-if="!isCollapsed" class="flex items-center">
									<div
										v-if="(user?.firstName && user?.lastName) || user?.profileImage"
										class="rounded-full text-white w-6 h-6 flex items-center justify-center text-xs overflow-hidden"
										:class="
											!user?.profileImage
												? isUserSectionActive()
													? 'bg-blue-600'
													: 'bg-yellow-500'
												: ''
										">
										<img
											class="rounded-full object-cover w-full h-full"
											v-if="user?.profileImage"
											:src="user?.profileImage" />
										<div v-else>
											{{ user?.firstName?.charAt(0) + user?.lastName?.charAt(0) }}
										</div>
									</div>
									<div v-else>
										<PhUser
											:class="['w-6 h-6', isUserSectionActive() ? 'text-blue-600' : '']"
											weight="light" />
									</div>
									<span :class="['sidebar-text', isUserSectionActive() ? 'text-blue-600' : '']">
										User
									</span>
								</div>
								<div
									v-else
									class="rounded-full text-white w-6 h-6 flex items-center justify-center text-xs overflow-hidden"
									:class="
										!user?.profileImage
											? isUserSectionActive()
												? 'bg-blue-600'
												: 'bg-yellow-500'
											: ''
									">
									<img
										v-if="user?.profileImage"
										class="rounded-full object-cover w-full h-full"
										:src="user?.profileImage" />
									<div v-else-if="user?.firstName && user?.lastName">
										{{ user?.firstName?.charAt(0) + user?.lastName?.charAt(0) }}
									</div>
									<PhUser v-else class="w-6 h-6 text-white" weight="light" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { h, ref, computed, onMounted, onUnmounted, watch } from 'vue'
// Phosphor icons
import {
	PhGraduationCap,
	PhArticle,
	PhCalendarCheck,
	PhUsersThree,
	PhChatCircleDots,
	PhBell,
	// PhMagnifyingGlass,
	PhGear,
	PhUser,
	PhSignOut,
	PhFilmStrip,
	PhBookmark,
	PhBuildings,
	PhDesktop,
	PhFileText,
	PhSparkle,
	PhFiles,
	PhCheckCircle,
	PhFolder,
	PhChartBar,
	PhArrowsClockwise,
	PhLock,
	PhList,
	PhChalkboardTeacher,
	PhHouseLine,
	PhCaretDown,
	PhCaretUp,
	PhMoon,
	PhSun,
	PhCaretLeft,
	PhCaretRight,
	PhEye,
	PhArrowsOut
} from '@phosphor-icons/vue'

// Naive UI components
import { NPopover, NMenu, NSwitch } from 'naive-ui'

import { useStore } from 'vuex'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { showDanger } from '@utils/AlertService'
import { auth } from '@root/firebaseDatabase'
import { AUTH_ERROR_CODES } from '@root/src/constants/Default'

// State management
const store = useStore()
const route = useRoute()
const router = useRouter()

// Define emits
const emit = defineEmits(['mobile-sidebar-state'])

const tenant = computed(() => store.state.tenant)
const user = computed(() => store.state.userData?.data)

const collapsed = ref(true)
const profileMenu = ref(false)
const root = ref('')
// const showNotify = ref(false)
const showSearch = ref(false)
const isMobile = ref(false)
const showMobileSidebar = ref(false)

// Mobile submenu expansion state
const mobileUserExpanded = ref(false)

// Full width state - store in Vuex to share with App.vue
const fullWidth = computed({
	get: () => store.state.fullWidth || false,
	set: (value) => store.commit('setFullWidth', value)
})

// Check if screen is 2xl or larger (1536px+)
const isLargeScreen = ref(false)

// Theme computed properties
const isDark = computed(() => store.getters['theme/isDark'])

// Computed property to handle collapsed state for mobile vs desktop
const isCollapsed = computed(() => {
	return isMobile.value ? false : collapsed.value
})

// Remove dynamic URL - we'll use simple paths like the old TopNav

// Add isActiveRoute function for highlighting
const isActiveRoute = (itemName) => {
	if (!route.path) {
		return false
	}

	// Special handling for Home and Dashboard
	if (itemName.toLowerCase() === 'home') {
		return route.path === '/'
	}

	if (itemName.toLowerCase() === 'dashboard') {
		return (
			(!tenant.value?.pages?.home && route.path === '/') ||
			route.path === '/dashboard' ||
			route.path.startsWith('/admin/dashboard')
		)
	}

	// Special handling for admin routes
	if (itemName.toLowerCase() === 'learning') {
		return route.path.startsWith('/admin/learning')
	}
	if (itemName.toLowerCase() === 'articles') {
		return route.path.startsWith('/admin/articles')
	}

	if (itemName.toLowerCase() === 'events') {
		return route.path.startsWith('/admin/events')
	}

	if (itemName.toLowerCase() === 'community') {
		return route.path.startsWith('/admin/community')
	}

	// Default case for other routes
	return route.path.startsWith(`/${itemName.toLowerCase()}`)
}

// Handle clicks on navigation items to disable search
const handleNavClick = () => {
	showSearch.value = false
	// Close mobile sidebar when navigating on mobile
	if (isMobile.value) {
		showMobileSidebar.value = false
		// Reset mobile expansion states
		mobileUserExpanded.value = false
	}
}

// Mobile detection function
const checkMobile = () => {
	isMobile.value = window.innerWidth < 768
	isLargeScreen.value = window.innerWidth >= 1536 // 2xl breakpoint
}

// Handle window resize for mobile detection
const handleResize = () => {
	checkMobile()
	// Close mobile sidebar when switching to desktop
	if (!isMobile.value) {
		showMobileSidebar.value = false
		// Emit false when switching to desktop
		emit('mobile-sidebar-state', false)
	}
}

// Reset mobile expansion states when mobile sidebar is closed
watch(showMobileSidebar, (newValue) => {
	if (!newValue) {
		mobileUserExpanded.value = false
		// Remove overflow hidden from body when closing sidebar
		document.body.style.overflow = ''
	} else {
		// Add overflow hidden to body when opening sidebar
		document.body.style.overflow = 'hidden'
	}

	// Emit mobile sidebar state to parent
	if (isMobile.value) {
		emit('mobile-sidebar-state', newValue)
	}
})

// Note: Community links now open in user app, so no active state needed

// Check if user section is active based on current route
const isUserSectionActive = () => {
	return route.path.startsWith('/user')
}

// User navigation menu options
const userNav = computed(() => [
	{
		label: () =>
			h('div', { class: 'flex items-center justify-between w-full' }, [
				h('div', { class: 'flex items-center gap-3' }, [
					h(isDark.value ? PhMoon : PhSun, { class: 'w-5 h-5 text-theme-muted', weight: 'light' }),
					h('span', { class: 'font-light text-base mr-2' }, 'Dark mode')
				]),
				h(NSwitch, {
					value: isDark.value,
					'onUpdate:value': toggleTheme,
					size: 'small'
				})
			]),
		key: 'darkmode',
		icon: () => null
	},
	// Full width toggle - only show on large screens
	...(isLargeScreen.value
		? [
				{
					label: () =>
						h('div', { class: 'flex items-center justify-between w-full' }, [
							h('div', { class: 'flex items-center gap-3' }, [
								h(PhArrowsOut, {
									class: 'w-5 h-5 text-theme-muted',
									weight: 'light'
								}),
								h('span', { class: 'font-light text-base mr-2' }, 'Full width')
							]),
							h(NSwitch, {
								value: fullWidth.value,
								'onUpdate:value': (value) => (fullWidth.value = value),
								size: 'small'
							})
						]),
					key: 'fullwidth',
					icon: () => null
				}
			]
		: []),
	{
		type: 'divider'
	},
	{
		label: () =>
			h(
				'a',
				{ href: '/user/profile', class: 'font-light text-base' },
				{ default: () => 'Profile' }
			),
		key: 'profile',
		icon: () => h(PhUser, { class: 'w-5 h-5 text-theme-muted', weight: 'light' })
	},
	{
		label: () =>
			h(
				'a',
				{ href: '/user/notifications', class: 'font-light text-base' },
				{ default: () => 'Notifications' }
			),
		key: 'notifications',
		icon: () => h(PhBell, { class: 'w-5 h-5 text-theme-muted', weight: 'light' })
	},
	{
		label: () =>
			h('a', { href: '/user/studio', class: 'font-light text-base' }, { default: () => 'Studio' }),
		key: 'studio',
		icon: () => h(PhFilmStrip, { class: 'w-5 h-5 text-theme-muted', weight: 'light' })
	},
	{
		label: () =>
			h(
				'a',
				{ href: '/user/bookmarks', class: 'font-light text-base' },
				{ default: () => 'Bookmarks' }
			),
		key: 'bookmarks',
		icon: () => h(PhBookmark, { class: 'w-5 h-5 text-theme-muted', weight: 'light' })
	},
	{
		label: () =>
			h(
				'a',
				{ href: '/user/company/profile', class: 'font-light text-base' },
				{ default: () => 'Company' }
			),
		key: 'company',
		icon: () => h(PhBuildings, { class: 'w-5 h-5 text-theme-muted', weight: 'light' })
	},
	{
		label: () =>
			h('span', { class: 'font-light text-base text-red-500' }, { default: () => 'Logout' }),
		key: 'logout',
		icon: () => h(PhSignOut, { class: 'w-5 h-5 text-red-500', weight: 'light' })
	}
])

// Close menus when clicking outside
const closeMenus = (event) => {
	// Close profile menu if clicking outside its areas
	if (
		profileMenu.value &&
		!event.target.closest('#profileMenu') &&
		!event.target.closest('#profileMenuTrigger')
	) {
		profileMenu.value = false
	}
}

// Handle profile menu item selection
const handleProfileMenuSelect = (key) => {
	profileMenu.value = false

	if (key === 'logout') {
		logout()
		return
	}

	if (key === 'darkmode') {
		// Dark mode toggle is handled by the switch component
		return
	}

	const routes = {
		profile: '/user/profile',
		notifications: '/user/notifications',
		studio: '/user/studio',
		bookmarks: '/user/bookmarks',
		company: '/user/company/profile'
	}

	if (routes[key]) {
		router.push(routes[key])
	}
}

// Handle profile icon click
const handleProfileClick = (event) => {
	event.preventDefault()
	event.stopPropagation()
	profileMenu.value = !profileMenu.value
	showSearch.value = false
}

// Handle profile mouseover - ensure only one menu is open
const handleProfileMouseOver = () => {
	profileMenu.value = true
}

// Logout function
const logout = async () => {
	await auth
		.signOut()
		.then(() => {
			store.dispatch('updateCurrentUser', undefined)
			store.commit('setLoginRedirect', undefined)
			store.dispatch('updateViewAs', undefined)
			store.dispatch('updateUser', undefined)
			store.dispatch('updateSideMenuView', undefined)
			window.open('/login', '_self')
		})
		.catch((error) => {
			console.error('🚀 ~ file: AdminSidebar.vue ~ logout ~ error', error)
			showDanger(AUTH_ERROR_CODES[error.code] || error.message, 5000)
		})
}

// Toggle theme - simple light/dark toggle (bypasses system theme state)
const toggleTheme = () => {
	const newTheme = isDark.value ? 'light' : 'dark'
	store.dispatch('theme/setTheme', newTheme)
}

onMounted(() => {
	// Set up event listener to close menus when clicking outside
	window.document.addEventListener('click', closeMenus)

	// Set up mobile detection
	checkMobile()
	window.addEventListener('resize', handleResize)

	// Initialize root for routing links
	root.value = window.location.host
})

onUnmounted(() => {
	window.document.removeEventListener('click', closeMenus)
	window.removeEventListener('resize', handleResize)

	// Clean up body overflow style
	document.body.style.overflow = ''
})
</script>

<style scoped>
:deep(.n-layout-sider-scroll-container) {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: visible;
}

/* Main content area with fixed header/footer and scrollable middle */
.sidebar-content-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
}

/* Middle scrollable section */
.sidebar-scrollable-section {
	flex: 1;
	overflow-y: auto;
	padding-bottom: 8px;
}

/* Footer that stays at bottom */
.sidebar-footer {
	flex-shrink: 0;
	padding: 12px;
	border-top: 1px solid rgba(0, 0, 0, 0.05);
	background-color: white;
	position: relative; /* For absolute positioning of menu */
}

.sidebar-link {
	display: flex;
	align-items: center;
	padding: 5px 5px;
	border-radius: 6px;
	cursor: pointer;
	white-space: nowrap;
	overflow: hidden;
	transition: all 0.2s ease;
}

.sidebar-icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
}

:deep(.n-menu-item-content-header) {
	color: #3b82f6;
}

.sidebar-text {
	margin-left: 12px;
	font-size: 14px;
	overflow: hidden;
	text-overflow: ellipsis;
}

/* Profile and admin dropdown menu styling */
#profileMenu,
#adminMenu {
	min-width: 180px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid rgba(0, 0, 0, 0.1);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Dark mode styles are now handled by theme classes */

/* Style fixes for Naive UI menu in sidebar context */
:deep(.n-menu-item) {
	padding: 8px 12px;
}

:deep(.n-menu-item-content__icon) {
	margin-right: 8px;
}

:deep(.n-menu-item-content-header a) {
	margin-right: 20px;
	color: #333;
}

/* Consistent text color across all menu items */
:deep(.n-menu-item-content),
:deep(.n-menu-item-content a) {
	color: #333;
}

:deep(.n-menu-item:hover .n-menu-item-content),
:deep(.n-menu-item:hover .n-menu-item-content a),
:deep(.n-menu-item:hover .n-menu-item-content-header) {
	color: #3b82f6;
}

:deep(.n-menu-item:hover .n-menu-item-content__icon) {
	color: #3b82f6;
}
</style>
